import { z } from 'zod';
import { isValidPhone } from './utils';

/**
 * Form → API contract.
 *
 * The shape lives here once. The API validates against `LeadSubmissionSchema`, and
 * every form that posts a lead types its body `satisfies LeadSubmissionPayload`, so a
 * renamed or missing field is a compile error rather than a 400 at runtime.
 */

export const FACILITY_TYPES = ['office', 'retail', 'medical', 'industrial', 'other'] as const;
export type FacilityType = (typeof FACILITY_TYPES)[number];

export const FREQUENCIES = ['biweekly', 'weekly', '3x', '5x'] as const;
export type Frequency = (typeof FREQUENCIES)[number];

export const LeadSubmissionSchema = z.object({
  // Required. The research is blunt: the phone number is the field that converts.
  name: z.string().trim().min(1, 'Please tell us your name').max(120),
  phone: z
    .string()
    .trim()
    .refine(isValidPhone, 'Please enter a 10-digit phone number'),

  // Everything else is optional — a short form beats a complete one.
  email: z.string().trim().email('Please check the email address').max(200).optional().or(z.literal('')),
  company: z.string().trim().max(160).optional(),
  zip: z.string().trim().max(10).optional(),
  facilityType: z.enum(FACILITY_TYPES).optional(),
  sqft: z.coerce.number().int().positive().max(10_000_000).optional(),
  frequency: z.enum(FREQUENCIES).optional(),
  message: z.string().trim().max(2000).optional(),

  // TCPA consent must be explicit and recorded.
  consent: z.boolean().default(false),

  sourcePage: z.string().max(300).optional(),
});

export type LeadSubmissionPayload = z.input<typeof LeadSubmissionSchema>;
export type LeadSubmission = z.output<typeof LeadSubmissionSchema>;

export interface LeadSubmissionResponse {
  ok: boolean;
  leadId?: number;
  error?: string;
  /** Field-level messages, keyed by the payload field name. */
  fieldErrors?: Record<string, string>;
}
